import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Icon, Menu, Modal, Pagination, Select, DropdownProps } from 'semantic-ui-react';
import styled from 'styled-components';
import { StyledButton } from 'common/components/button/StyledButton';
import { FileTypes, getFileTypeIcon } from 'common/constants/filetypes';
import { getFileSizeFormatted, getFileLabelFromType } from 'areas/settings/repository-management/utils/LabelUtils';
import { FileViewModal } from 'areas/settings/repository-management/components/FileViewModal';
import KnowledgeBaseContentShareModal from './KnowledgeBaseContentShareModal';
import { Caption, PageContent, PageHeader, PageSection, Title } from 'common/styles/page';
import { HeaderCell, Table, TableBody, TableCell, TableHeader, TableRow } from 'common/components/Table';
import PositionedModal from 'common/components/PositionedModal';
import LoadingSpinner from 'common/components/LoadingSpinner';
import repositoryApi, { ResourceCategoryRead, useGetFileUploadsQuery, useGetResourceCategoriesQuery } from 'areas/settings/repository-management/api/repositoryApi';
import { RepositoryFileRead } from '../../../store/generated/admin/repository-files';
import { AppConfig } from '../../../Config';
import { authenticatedFetch } from 'common/utils/authFetch';
import { isYouTubeWatchUrl, openResourceLink } from 'common/utils/resourceLinks';
import { useSelector } from 'react-redux';
import { getCurrentOrganizationId } from 'common/selectors/auth';

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border-radius: 5px;
  border: 1px solid #d9d9d9;
  background-color: #fff;
  font-size: 14px;
  color: #111;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #5f7fff;
    box-shadow: 0 0 0 3px rgba(95, 127, 255, 0.16);
  }
`;

const FilterSelect = styled(Select)`
  &&& {
    min-height: 44px;
    display: flex;
    align-items: center;
  }

  &&& .text {
    line-height: 1.2;
    padding-top: 0;
    padding-bottom: 0;
  }
`;

const ResourceListPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [viewFileId, setViewFileId] = useState<number | null>(null);
  const [viewFileName, setViewFileName] = useState<string>('');
  const [isFileViewOpen, setFileViewOpen] = useState(false);
  const [selectedResourceDetails, setSelectedResourceDetails] = useState<RepositoryFileRead | null>(null);
  const [isDetailsModalOpen, setDetailsModalOpen] = useState(false);
  const [shareResource, setShareResource] = useState<RepositoryFileRead | null>(null);
  const [isShareModalOpen, setShareModalOpen] = useState(false);
  const [activePage, setActivePage] = useState(1);

  const ALL_FILE_TYPES = '-1';
  const [typeFilter, setTypeFilter] = useState<string>(ALL_FILE_TYPES);
  const [searchText, setSearchText] = useState('');

  const selectedCategory = searchParams.get('category');
  const { data: categoryData } = useGetResourceCategoriesQuery();

  const rawCategoryData = categoryData as unknown;
  const categories: ResourceCategoryRead[] = Array.isArray(rawCategoryData)
    ? (rawCategoryData as ResourceCategoryRead[])
    : Array.isArray((rawCategoryData as { results?: ResourceCategoryRead[] })?.results)
      ? (rawCategoryData as { results: ResourceCategoryRead[] }).results
      : [];

  const selectedCategoryDetails = selectedCategory
    ? categories.find(category => category.slug === selectedCategory)
    : undefined;
  const selectedCategoryId = selectedCategoryDetails?.id;

  const { data, isLoading, error } = useGetFileUploadsQuery(
    selectedCategoryId ? { category_id: selectedCategoryId } : undefined
  );

  useEffect(() => {
    setActivePage(1);
  }, [typeFilter, searchText, selectedCategory]);

  const rawResourceData = data as unknown;
  const resources: RepositoryFileRead[] = Array.isArray(rawResourceData)
    ? (rawResourceData as RepositoryFileRead[])
    : Array.isArray((rawResourceData as { results?: RepositoryFileRead[] })?.results)
      ? (rawResourceData as { results: RepositoryFileRead[] }).results
      : [];

  const selectedCategoryName = selectedCategoryDetails?.name;
  const selectedCategoryIcon = selectedCategoryDetails?.icon;

  const formatSlug = (slug: string) =>
    slug
      .split(/[-_]/)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

  const pageTitle = selectedCategory
    ? selectedCategoryName || formatSlug(selectedCategory)
    : 'Knowledge Base';

  const currentOrganizationId = useSelector(getCurrentOrganizationId);

  const recordLinkOpen = async (url: string, resourceName?: string, repositoryFileId?: number) => {
    if (!currentOrganizationId) {
      return;
    }

    const apiUrl = AppConfig.api.apiUrl || '';
    const endpoint = `${apiUrl}/api/portal/v1/assessments/leaderboard-events/link-open/`;

    try {
      await authenticatedFetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-NULOGIC-ORGANIZATION-ID': String(currentOrganizationId),
        },
        body: JSON.stringify({
          url,
          resource_name: resourceName,
          repository_file_id: repositoryFileId,
        }),
      });
    } catch (error) {
      console.warn('Failed to record link open leaderboard event', error);
    }
  };

  const isYouTubeResource = (resource: RepositoryFileRead) =>
    resource.type === FileTypes.URL && isYouTubeWatchUrl(resource.object_key ?? undefined);

  const filteredResources = (selectedCategoryId
    ? resources.filter(resource =>
      resource.category_ids?.includes(selectedCategoryId)
    )
    : resources
  )
    .filter(resource => {
      if (typeFilter === ALL_FILE_TYPES) {
        return true;
      }

      if (typeFilter === FileTypes.MP4.toString()) {
        return resource.type === FileTypes.MP4 || isYouTubeResource(resource);
      }

      if (typeFilter === FileTypes.URL.toString()) {
        return resource.type === FileTypes.URL && !isYouTubeResource(resource);
      }

      return resource.type?.toString() === typeFilter;
    })
    .filter(resource => {
      const query = searchText.trim().toLowerCase();
      if (!query) return true;
      const text = `${resource.name ?? ''} ${resource.description ?? ''}`.toLowerCase();
      return text.includes(query);
    });
  const itemsPerPage = 10;
  const paginatedResources = filteredResources.slice((activePage - 1) * itemsPerPage, activePage * itemsPerPage);
  const emptyMessage = selectedCategory
    ? 'No resources found for this category.'
    : 'No resources found.';

  const handleResourceClick = (resource: RepositoryFileRead) => {
    openResourceLink(resource, {
      recordLinkOpen: (url, resourceName, repositoryFileId) => {
        void recordLinkOpen(url, resourceName, repositoryFileId);
      },
      openInApp: () => {
        setViewFileName(resource.name);
        setViewFileId(resource.id);
        setFileViewOpen(true);
      },
      openInNewTab: url => window.open(url, '_blank', 'noopener'),
    });
  };

  const renderResourceIcon = (resource: RepositoryFileRead) => {
    const isYouTubeLink = isYouTubeResource(resource);
    if (isYouTubeLink) {
      return getFileTypeIcon(FileTypes.MP4, { size: 'big' });
    }

    return getFileTypeIcon(resource.type ?? FileTypes.INVALID_FILE_TYPE, { size: 'big' });
  };

  const renderResourceType = (resource: RepositoryFileRead) => {
    if (isYouTubeResource(resource)) {
      return (
        <ResourceTypeInline>
          <Icon name="youtube" color="red" />
          <span>YouTube</span>
        </ResourceTypeInline>
      );
    }

    return getResourceTypeLabel(resource);
  };

  const handleDetailsClick = (resource: RepositoryFileRead) => {
    setSelectedResourceDetails(resource);
    setDetailsModalOpen(true);
  };

  const handleDetailsClose = () => {
    setSelectedResourceDetails(null);
    setDetailsModalOpen(false);
  };

  const handleShareClick = (resource: RepositoryFileRead) => {
    setShareResource(resource);
    setShareModalOpen(true);
  };

  const getResourceTypeLabel = (resource: RepositoryFileRead) => {
    const typeLabel = resource.type === FileTypes.URL ? 'Web Link' : getFileLabelFromType(resource.type ?? -1);
    if (resource.type === FileTypes.URL) {
      return typeLabel;
    }
    return `${typeLabel} (${getFileSizeFormatted(resource.size ?? 0)})`;
  };

  const handlePageChange = (_: any, { activePage: page }: any) => {
    setActivePage(page);
  };

  const handleReturnToBasePage = () => {
    navigate('/app/knowledge-base');
  };

  return (
    <PageContent>
      <PageSection>

        <PageHeader
          titleIcon={selectedCategoryIcon ? <Icon name={selectedCategoryIcon as any} /> : undefined}
          title={pageTitle}
          caption="Browse available knowledge base resources. Click any row to view or open the resource."
          backText="Return to Knowledge Base"
          onBack={handleReturnToBasePage}
        />

      </PageSection>
      <PageSection padtop>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <SearchInput
            id="resource-table-search"
            type="search"
            placeholder="Search by name or description"
            value={searchText}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchText(event.target.value)}
            aria-label="Search resources"
            spellCheck={false}
            style={{ flex: '1 1 320px', minWidth: '260px' }}
          />
          <FilterSelect
            placeholder="Filter by Type"
            options={[
              { key: 'all', value: ALL_FILE_TYPES, text: 'All Files' },
              { key: 'pdf', value: FileTypes.PDF.toString(), text: 'Documents' },
              { key: 'mp4', value: FileTypes.MP4.toString(), text: 'Videos' },
              { key: 'url', value: FileTypes.URL.toString(), text: 'Links' },
            ]}
            value={typeFilter}
            onChange={(_e: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => setTypeFilter(data.value as string)}
          />
        </div>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem 0' }}>
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div>Unable to load resources. Please try again later.</div>
        ) : (
          <>
            <DesktopTableWrapper>
              <Table selectable>
                <TableHeader>
                  <Table.Row>
                    <HeaderCell width={5}>Name</HeaderCell>
                    <HeaderCell width={7}>Description</HeaderCell>
                    <HeaderCell width={2}>Resource</HeaderCell>
                    <HeaderCell width={1}></HeaderCell>
                  </Table.Row>
                </TableHeader>
                <TableBody>
                  {filteredResources.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan="5" style={{ textAlign: 'center', padding: '2rem 2rem 2rem 3rem', color: '#666' }}>
                        {emptyMessage}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedResources.map(resource => (
                      <TableRow
                        key={resource.id}
                        $selectable
                        onClick={() => handleResourceClick(resource)}
                      >
                        <TableCell $firstcol style={{ verticalAlign: 'top' }} fontSize="14px">
                          <ResourceRowContent>
                            <IconWrapper>
                              {renderResourceIcon(resource)}
                            </IconWrapper>
                            <ResourceNameBlock>
                              <ResourceTitleRow>
                                {resource.name}
                              </ResourceTitleRow>
                              <ResourceMetaText title={resource.source || resource.object_key || undefined}>
                                {resource.source
                                  ? resource.source.length > 40
                                    ? `${resource.source.slice(0, 40)}...${resource.source.slice(-4)}`
                                    : resource.source
                                  : resource.object_key?.length > 40
                                    ? `${resource.object_key.slice(0, 40)}...${resource.object_key.slice(-4)}`
                                    : resource.object_key}
                              </ResourceMetaText>
                            </ResourceNameBlock>
                          </ResourceRowContent>
                        </TableCell>
                        <TableCell style={{ verticalAlign: 'top' }}>
                          <ResourceDescriptionText title={resource.description || 'No description'}>
                            {resource?.description
                              ? resource.description.length > 200
                                ? `${resource.description.slice(0, 200)}...`
                                : resource.description
                              : 'No description'}
                          </ResourceDescriptionText>
                        </TableCell>
                        <TableCell>{renderResourceType(resource)}</TableCell>
                        <TableCell style={{ display: 'flex', gap: '6px', flexWrap: 'nowrap', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                          <Button
                            icon
                            basic
                            title="Share Resource"
                            aria-label="Share Resource"
                            style={{ width: 36, height: 36, minWidth: 36, padding: 0 }}
                            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                              event.stopPropagation();
                              handleShareClick(resource);
                            }}
                          >
                            <Icon name="share" size="large" />
                          </Button>
                          <Button
                            icon
                            basic
                            title="More Information"
                            aria-label="More Information"
                            style={{ width: 36, height: 36, minWidth: 36, padding: 0 }}
                            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                              event.stopPropagation();
                              handleDetailsClick(resource);
                            }}
                          >
                            <Icon name="info circle" size="large" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </DesktopTableWrapper>

            <MobileCardList>
              {filteredResources.length === 0 ? (
                <EmptyMobileMessage>{emptyMessage}</EmptyMobileMessage>
              ) : (
                paginatedResources.map(resource => (
                  <MobileCard key={resource.id} onClick={() => handleResourceClick(resource)}>
                    <MobileCardHeader>
                      {renderResourceIcon(resource)}
                      <MobileCardTitle>
                        <ResourceTitleRow>{resource.name}</ResourceTitleRow>
                      </MobileCardTitle>
                    </MobileCardHeader>
                    <MobileCardBody>
                      <CardRow>
                        <CardLabel>Resource</CardLabel>
                        <CardValue>{renderResourceType(resource)}</CardValue>
                      </CardRow>
                      <CardRow>
                        <CardLabel>Description</CardLabel>
                        <CardValue>
                          {resource?.description
                            ? resource.description.length > 120
                              ? `${resource.description.slice(0, 120)}...`
                              : resource.description
                            : 'No description'}
                        </CardValue>
                      </CardRow>
                      <MobileCardActions>
                        <IconButton
                          title="Share Resource"
                          aria-label="Share Resource"
                          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                            event.stopPropagation();
                            handleShareClick(resource);
                          }}
                        >
                          <Icon name="share" size="large" />
                        </IconButton>
                        <IconButton
                          title="More Information"
                          aria-label="More Information"
                          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                            event.stopPropagation();
                            handleDetailsClick(resource);
                          }}
                        >
                          <Icon name="info circle" size="large" />
                        </IconButton>
                      </MobileCardActions>
                    </MobileCardBody>
                  </MobileCard>
                ))
              )}
            </MobileCardList>

            {filteredResources.length > itemsPerPage && (
              <Menu pagination>
                <Pagination
                  totalPages={Math.ceil(filteredResources.length / itemsPerPage)}
                  activePage={activePage}
                  onPageChange={handlePageChange}
                  boundaryRange={1}
                  size="mini"
                  firstItem={null}
                  lastItem={null}
                />
              </Menu>
            )}
          </>
        )}
      </PageSection>

      {viewFileId !== null && (
        <FileViewModal
          title={viewFileName}
          fileId={viewFileId.toString()}
          isOpen={isFileViewOpen}
          setModalOpen={setFileViewOpen}
        />
      )}

      <KnowledgeBaseContentShareModal
        fileId={shareResource?.id ?? 0}
        fileName={shareResource?.name ?? ''}
        isOpen={isShareModalOpen}
        setModalOpen={setShareModalOpen}
      />

      <PositionedModal open={isDetailsModalOpen} onClose={handleDetailsClose} size="small" closeIcon>
        <Modal.Header>Resource Details</Modal.Header>
        <Modal.Content>
          <ModalBody>
            <DetailRow>
              <DetailLabel>Name</DetailLabel>
              <DetailValue>{selectedResourceDetails?.name || '—'}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Source</DetailLabel>
              <DetailValue>{selectedResourceDetails?.source || '—'}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Resource Type</DetailLabel>
              <DetailValue>{getFileLabelFromType(selectedResourceDetails?.type ?? -1)}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Themes</DetailLabel>
              <DetailValue>{selectedResourceDetails?.tags || '—'}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Outcomes</DetailLabel>
              <DetailValue>{selectedResourceDetails?.outcomes || '—'}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>AI Overview</DetailLabel>
              <ClampedDetailValue>{selectedResourceDetails?.ai_overview || '—'}</ClampedDetailValue>
            </DetailRow>
          </ModalBody>
        </Modal.Content>
      </PositionedModal>
    </PageContent>
  );
};

const ResourceRowContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  min-width: 0;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  flex: 0 0 auto;
`;

const ResourceNameBlock = styled.div`
  min-width: 0;
`;

const ResourceTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 1.75rem;
`;

const ResourceMetaText = styled.div`
  font-size: 0.85em;
  color: grey;
  margin-top: 0.25rem;
`;

const ResourceDescriptionText = styled.div`
  font-size: 0.95em;
  color: grey;
  margin-top: 2px;
  padding-right: 1rem;
`;

const DesktopTableWrapper = styled.div`
  @media (max-width: 900px) {
    display: none;
  }
`;

const MobileCardList = styled.div`
  display: none;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 900px) {
    display: flex;
  }
`;

const EmptyMobileMessage = styled.div`
  padding: 1.5rem;
  color: #666;
  text-align: center;
`;

const MobileCard = styled.div`
  border: 1px solid #d4d4d5;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  background: #fff;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 12px rgba(0, 0, 0, 0.12);
  }
`;

const MobileCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border-bottom: 1px solid #eff2f7;
`;

const MobileCardTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const MobileCardBody = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CardRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: flex-start;
`;

const CardLabel = styled.div`
  min-width: 90px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
`;

const CardValue = styled.div`
  flex: 1;
  font-size: 14px;
  color: #334155;
  white-space: pre-wrap;
`;

const ResourceTypeInline = styled.span`
  display: inline-flex;
  align-items: center;
  line-height: 1;

  i.icon {
    margin-right: 0.35em !important;
    font-size: 1.25em !important;
    line-height: 1 !important;
    margin-top: 0 !important;
  }

  span {
    line-height: 1;
  }
`;

const MobileCardActions = styled.div`
  display: flex;
  gap: 12px;
  padding-top: 4px;
`;

const IconButton = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  min-width: 40px;
  padding: 0;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #d4d4d5;
  color: #1f2937;
  cursor: pointer;

  &:hover {
    background: #eef2ff;
  }
`;

const ModalBody = styled.div`
  display: grid;
  gap: 18px;
  padding: 16px 0;
`;

const DetailRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 12px;
  align-items: flex-start;
`;

const DetailLabel = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #333;
`;

const DetailValue = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #444;
  white-space: pre-wrap;
`;

const ClampedDetailValue = styled(DetailValue)`
  display: -webkit-box;
  -webkit-line-clamp: 10;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: calc(1.5em * 10);
  line-height: 1.5;
`;

export default ResourceListPage;
